import { Fragment, useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { EXECUTION_STATUS_LABELS, EXECUTION_STATUS_COLORS, NODE_TYPE_COLORS } from '@/types/docflow';
import type { IExecutionResult, IOutputField, NodeType } from '@/types/docflow';
import DocumentAnnotationViewer from './DocumentAnnotationViewer';

interface ExecutionResultsTableProps {
  results: IExecutionResult[];
  fields?: IOutputField[];
}

function formatFileSize(bytes: unknown): string {
  const size = Number(bytes);
  if (!size || isNaN(size)) return '—';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

interface NodeCardProps {
  nodeType: NodeType;
  icon: string;
  title: string;
  children: React.ReactNode;
}

const NodeCard: React.FC<NodeCardProps> = ({ nodeType, icon, title, children }) => {
  const colors = NODE_TYPE_COLORS[nodeType];
  return (
    <div className={`rounded-lg border ${colors.border} ${colors.bg} p-3 min-w-[140px]`}>
      <div className={`text-xs font-semibold ${colors.text} mb-1`}>
        {icon} {title}
      </div>
      <div className="text-xs text-gray-600 dark:text-gray-400">{children}</div>
    </div>
  );
};

const Arrow: React.FC = () => (
  <div className="flex items-center px-1 text-gray-400 dark:text-gray-500 shrink-0">
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <path d="M4 10h12m0 0l-4-4m4 4l-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  </div>
);

const ExecutionResultsTable: React.FC<ExecutionResultsTableProps> = ({ results, fields }) => {
  const { t } = useTranslation('docflow');
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);
  const [showRawJson, setShowRawJson] = useState<string | null>(null);
  const [showAnnotations, setShowAnnotations] = useState<string | null>(null);

  // Derive columns from fields or from first result's extracted data keys
  const columns = useMemo(() => {
    if (fields && fields.length > 0) {
      return fields;
    }
    // Fallback: extract keys from first successful result
    const firstSuccess = results.find((r) => r.extracted_data && Object.keys(r.extracted_data).length > 0);
    if (firstSuccess?.extracted_data) {
      return Object.keys(firstSuccess.extracted_data).map((key) => ({
        key,
        label: key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
        type: 'string' as const,
        required: false,
      }));
    }
    return [];
  }, [results, fields]);

  const handleExportCsv = useCallback(() => {
    const headers = [t('execution.fileName'), t('execution.status'), ...columns.map((c) => c.label)];
    const rows = results.map((r) => [
      r.file_name,
      r.status,
      ...columns.map((c) => {
        const val = r.extracted_data?.[c.key];
        return val == null ? '' : String(val);
      }),
    ]);

    const csv = [headers, ...rows].map((row) =>
      row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(',')
    ).join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'docflow-results.csv';
    a.click();
    URL.revokeObjectURL(url);
  }, [results, columns, t]);

  const toggleRow = useCallback((id: string) => {
    setExpandedRowId((prev) => (prev === id ? null : id));
    setShowRawJson(null);
  }, []);

  const totalCols = columns.length + 3; // fileName + status + columns + error

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
          {t('execution.results')} ({results.length})
        </h3>
        {results.length > 0 && (
          <button
            onClick={handleExportCsv}
            className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600"
          >
            {t('execution.exportCsv')}
          </button>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 dark:border-gray-700">
              <th className="text-left py-2 px-3 font-medium text-gray-600 dark:text-gray-400">
                {t('execution.fileName')}
              </th>
              <th className="text-left py-2 px-3 font-medium text-gray-600 dark:text-gray-400">
                {t('execution.status')}
              </th>
              {columns.map((col) => (
                <th key={col.key} className="text-left py-2 px-3 font-medium text-gray-600 dark:text-gray-400">
                  {col.label}
                </th>
              ))}
              <th className="text-left py-2 px-3 font-medium text-gray-600 dark:text-gray-400">
                {t('execution.error')}
              </th>
            </tr>
          </thead>
          <tbody>
            {results.map((result) => {
              const isExpanded = expandedRowId === result.id;
              const nodeOutputs = result.node_outputs;
              const hasNodeOutputs = nodeOutputs && Object.keys(nodeOutputs).length > 0;
              const driveInput = nodeOutputs?.drive_input;
              const visionOutput = nodeOutputs?.claude_vision;
              const sheetOutput = nodeOutputs?.sheet_output;
              const driveMove = nodeOutputs?.drive_move;
              const extractedFieldCount = result.extracted_data ? Object.keys(result.extracted_data).length : 0;

              return (
                <Fragment key={result.id}>
                  <tr
                    className={`border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 ${hasNodeOutputs ? 'cursor-pointer' : ''}`}
                    onClick={hasNodeOutputs ? () => toggleRow(result.id) : undefined}
                  >
                    <td className="py-2 px-3 text-gray-700 dark:text-gray-300 max-w-[200px] truncate">
                      <span className="inline-flex items-center gap-1">
                        {hasNodeOutputs && (
                          <span className="text-gray-400 text-xs">{isExpanded ? '\u25BC' : '\u25B6'}</span>
                        )}
                        {result.file_name}
                      </span>
                    </td>
                    <td className="py-2 px-3">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${EXECUTION_STATUS_COLORS[result.status]}`}>
                        {EXECUTION_STATUS_LABELS[result.status]}
                      </span>
                    </td>
                    {columns.map((col) => (
                      <td key={col.key} className="py-2 px-3 text-gray-700 dark:text-gray-300 max-w-[200px] truncate">
                        {result.extracted_data?.[col.key] != null
                          ? String(result.extracted_data[col.key])
                          : '\u2014'}
                      </td>
                    ))}
                    <td className="py-2 px-3 text-red-500 text-xs max-w-[200px] truncate">
                      {result.error || ''}
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr key={`${result.id}-detail`} className="border-b border-gray-100 dark:border-gray-800">
                      <td colSpan={totalCols} className="p-0">
                        <div className="bg-gray-50 dark:bg-gray-900/50 border-t border-gray-200 dark:border-gray-700 px-4 py-3">
                          <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
                            {t('execution.nodePipeline')}
                          </div>

                          {/* Node pipeline cards */}
                          <div className="flex items-center gap-0 overflow-x-auto pb-2">
                            {driveInput && (
                              <>
                                <NodeCard nodeType="drive_input" icon="📁" title={t('node.driveInput')}>
                                  <div className="font-medium truncate max-w-[160px]">{String(driveInput.file_name || '')}</div>
                                  <div>{String(driveInput.mime_type || '').split('/').pop()?.toUpperCase()} · {formatFileSize(driveInput.file_size)}</div>
                                </NodeCard>
                                <Arrow />
                              </>
                            )}

                            {visionOutput && (
                              <>
                                <NodeCard nodeType="claude_vision" icon="🤖" title={t('node.claudeVision')}>
                                  <div className="font-medium">{String(visionOutput.model || '')}</div>
                                  <div>{t('execution.provider')}: {String(visionOutput.provider || '')}</div>
                                </NodeCard>
                                <Arrow />
                              </>
                            )}

                            <NodeCard nodeType="output_designer" icon="📋" title={t('node.outputDesigner')}>
                              <div className="font-medium">{extractedFieldCount} {t('execution.fieldsExtracted')}</div>
                            </NodeCard>

                            {sheetOutput && (
                              <>
                                <Arrow />
                                <NodeCard nodeType="sheet_output" icon="📊" title={t('node.sheetOutput')}>
                                  <div className="font-medium truncate max-w-[160px]">{String(sheetOutput.sheet_name || '')}</div>
                                  <div>{t('execution.sheetWritten')}</div>
                                </NodeCard>
                              </>
                            )}

                            {driveMove && (
                              <>
                                <Arrow />
                                <NodeCard nodeType="drive_move" icon="📦" title={t('node.driveMove')}>
                                  <div>{t('execution.fileMoved')}</div>
                                </NodeCard>
                              </>
                            )}
                          </div>

                          {/* Extracted data JSON toggle + Annotations toggle */}
                          {result.extracted_data && extractedFieldCount > 0 && (
                            <div className="mt-2 flex flex-wrap gap-3">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setShowRawJson((prev) => (prev === result.id ? null : result.id));
                                }}
                                className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 underline"
                              >
                                {t('execution.rawJson')} {showRawJson === result.id ? '\u25B2' : '\u25BC'}
                              </button>

                              {/* Show Annotations toggle — only when field_locations exist and mime is image */}
                              {visionOutput?.field_locations &&
                                typeof visionOutput.field_locations === 'object' &&
                                Object.keys(visionOutput.field_locations as Record<string, unknown>).length > 0 &&
                                driveInput?.mime_type &&
                                String(driveInput.mime_type).startsWith('image/') && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setShowAnnotations((prev) => (prev === result.id ? null : result.id));
                                  }}
                                  className="text-xs text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-200 underline"
                                >
                                  {showAnnotations === result.id
                                    ? t('execution.hideAnnotations')
                                    : t('execution.showAnnotations')}
                                </button>
                              )}
                            </div>
                          )}

                          {showRawJson === result.id && result.extracted_data && (
                            <pre className="mt-1 p-2 bg-gray-100 dark:bg-gray-800 rounded text-xs text-gray-700 dark:text-gray-300 overflow-x-auto max-h-[200px] overflow-y-auto">
                              {JSON.stringify(result.extracted_data, null, 2)}
                            </pre>
                          )}

                          {/* Document annotation overlay */}
                          {showAnnotations === result.id && visionOutput?.field_locations && driveInput?.mime_type && (
                            <DocumentAnnotationViewer
                              fileId={result.file_id}
                              mimeType={String(driveInput.mime_type)}
                              fieldLocations={visionOutput.field_locations as Record<string, unknown>}
                              extractedData={result.extracted_data || {}}
                              fields={fields}
                            />
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ExecutionResultsTable;
